import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { fmt, prettyModelId, tierOfModel, TIER_COLORS } from "@/lib/format";
import ChartFrame from "@/components/ChartFrame";
import { endpointDot } from "@/lib/chartStyle";

// "사용량" 탭의 모델별 뷰(2026-07-18) — 도구별 뷰와 같은 자리에서 절단면만 바꿔 본다
// (도구=무엇을 했나, 모델=어떤 엔진이 일했나). 차트는 모델별 호출 수 추이(부모가 이미
// 받아둔 modelCostSeries의 byModelCount 재집계본), 표는 /analytics/by-model(호출 수·
// NC 사용·토큰·캐시히트). 같은 티어(sonnet 등)의 모델은 같은 계열 색으로 표시.
// filterActive: 부모(사용량 탭)의 도구 필터가 좁혀져 있는지 — 차트는 필터가 반영된
// series 를 받지만, 하단 상세 표(/analytics/by-model)는 아직 전체 기준이라 필터 중일 땐
// 그 사실을 표 위에 밝힌다(숫자가 서로 달라 보이는 오해 방지).
export default function ModelUsageView({ series, range, filterActive = false }) {
  // 범례 클릭 = 라인 켜고 끄기(2026-07-18 — 다른 추이 차트와 동일한 동작).
  const [hidden, setHidden] = useState(new Set());
  const { data: byModelData, loading, error } = useAnalyticsQuery(
    (p, s) => analyticsApi.byModel(p, s),
    [range.from, range.to],
    range
  );

  const rows = series || [];
  const models = [...new Set(rows.flatMap((r) => Object.keys(r.byModel || {})))];
  const totals = {};
  for (const r of rows) for (const [m, c] of Object.entries(r.byModel || {})) totals[m] = (totals[m] || 0) + c;
  models.sort((a, b) => (totals[b] || 0) - (totals[a] || 0));

  const chartData = [...rows].reverse().map((r) => {
    const point = { bucket: r.bucketLabel || r.bucket };
    models.forEach((m) => { point[prettyModelId(m)] = r.byModel?.[m] || 0; });
    return point;
  });

  const tableRows = [...(byModelData?.models || [])].sort((a, b) => (b.requestCount || 0) - (a.requestCount || 0));

  return (
    <>
      {rows.length === 0 ? (
        <div className="rounded-lg border border-ink-200 bg-white p-6 text-center text-sm text-ink-400">데이터 없음</div>
      ) : (
        <ChartFrame base="h-56 sm:h-80" tall="h-96 sm:h-[34rem]">
          <ResponsiveContainer width="100%" height="100%">
            {/* 차트 소프트닝(2026-07-18) — RevenueCostChart 와 공통 스타일. */}
            <LineChart data={chartData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f0f0ee" />
              <XAxis dataKey="bucket" tick={{ fill: "#8e8e8a", fontSize: 11 }} axisLine={false} tickLine={false} tickMargin={8} />
              <YAxis tick={{ fill: "#8e8e8a", fontSize: 11 }} allowDecimals={false} width={40} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 16px rgba(15,23,42,0.12)", fontSize: 12 }} />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 8, cursor: "pointer" }}
                onClick={(e) => {
                  if (!e.dataKey) return;
                  setHidden((prev) => {
                    const next = new Set(prev);
                    if (next.has(e.dataKey)) next.delete(e.dataKey);
                    else next.add(e.dataKey);
                    return next;
                  });
                }}
                formatter={(value, entry) => (
                  <span style={{ opacity: hidden.has(entry.dataKey) ? 0.4 : 1, textDecoration: hidden.has(entry.dataKey) ? "line-through" : "none" }}>
                    {value}
                  </span>
                )}
              />
              {models.map((m) => {
                const color = TIER_COLORS[tierOfModel(m)] || "#94a3b8";
                return (
                  <Line
                    key={m}
                    type="monotone"
                    dataKey={prettyModelId(m)}
                    stroke={color}
                    strokeWidth={2}
                    dot={endpointDot(color, chartData.length)}
                    activeDot={{ r: 4 }}
                    hide={hidden.has(prettyModelId(m))}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </ChartFrame>
      )}

      <div className="mt-4">
        {filterActive && (
          <p className="mb-2 text-xs text-ink-400">
            위 차트는 도구 필터가 적용된 숫자, 아래 상세 표는 이 기간 전체 기준입니다.
          </p>
        )}
        {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error.message}</div>}
        {!error && loading && !byModelData && <div className="text-sm text-ink-400">불러오는 중…</div>}
        {!error && tableRows.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-ink-100">
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-ink-500">모델</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-ink-500">호출 수</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-ink-500">크레딧 사용</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-ink-500">토큰(입력/출력)</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-ink-500">캐시히트%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {tableRows.map((m) => (
                  <tr key={m.modelId} className="hover:bg-ink-50">
                    <td className="flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-ink-700">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: TIER_COLORS[m.tier] || "#94a3b8" }} />
                      {prettyModelId(m.modelId)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-900">{fmt.int(m.requestCount)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-600">{fmt.nc(m.ncCost)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-500">
                      {fmt.compact(m.inputTokens)} / {fmt.compact(m.outputTokens)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-500">{fmt.pct(m.cacheHitRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
